import { useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Mail, MapPin, Instagram, Facebook } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    try {
      setLoading(true);
      await api.post("/newsletter", { email });
      toast.success("You're subscribed! Welcome to the Book Club family.");
      setEmail("");
    } catch (err) {
      toast.error("Subscription failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="mt-24 bg-brand-dark text-white/90">
      <div className="container-wrap py-16 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="flex items-center gap-2.5 mb-4">
            <span className="w-10 h-10 rounded-full bg-brand-gold text-brand-dark flex items-center justify-center">
              <BookOpen className="w-5 h-5" />
            </span>
            <span className="font-serif-display text-2xl text-white">WHO DAT Cares Foundation</span>
          </div>
          <p className="text-white/70 max-w-md leading-relaxed">
            Sparking a lifelong love for reading in New Orleans children — one book, one story, one young author at a time.
          </p>
          <div className="flex gap-3 mt-6">
            <a href="#" className="w-9 h-9 rounded-full bg-white/10 hover:bg-brand-gold hover:text-brand-dark flex items-center justify-center transition" data-testid="footer-instagram-link" aria-label="Instagram"><Instagram className="w-4 h-4" /></a>
            <a href="#" className="w-9 h-9 rounded-full bg-white/10 hover:bg-brand-gold hover:text-brand-dark flex items-center justify-center transition" data-testid="footer-facebook-link" aria-label="Facebook"><Facebook className="w-4 h-4" /></a>
          </div>
        </div>

        <div className="md:col-span-3">
          <h4 className="text-brand-gold uppercase text-xs tracking-[0.22em] font-semibold mb-4">Explore</h4>
          <ul className="space-y-2.5 text-sm">
            <li><Link to="/about" data-testid="footer-about-link" className="hover:text-brand-gold transition">About Us</Link></li>
            <li><Link to="/events" data-testid="footer-events-link" className="hover:text-brand-gold transition">Events</Link></li>
            <li><Link to="/shop" data-testid="footer-shop-link" className="hover:text-brand-gold transition">Shop</Link></li>
            <li><Link to="/donate" data-testid="footer-donate-link" className="hover:text-brand-gold transition">Donate</Link></li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <h4 className="text-brand-gold uppercase text-xs tracking-[0.22em] font-semibold mb-4">Stay in the Loop</h4>
          <form onSubmit={subscribe} className="flex gap-2">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              data-testid="newsletter-email-input"
              className="flex-1 bg-white/10 border border-white/20 rounded-full px-4 py-2.5 text-sm placeholder:text-white/40 focus:outline-none focus:border-brand-gold"
            />
            <button type="submit" disabled={loading} data-testid="newsletter-submit-button" className="bg-brand-gold text-brand-dark px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-brand-gold-soft transition disabled:opacity-60">
              {loading ? "..." : "Join"}
            </button>
          </form>
          <div className="mt-6 space-y-2 text-sm text-white/70">
            <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-brand-gold" /> WHO DAT BarberShop Library, New Orleans, LA</p>
            <p className="flex items-center gap-2"><Mail className="w-4 h-4 text-brand-gold" /> hello@whodatcares.org</p>
          </div>
        </div>
      </div>
      <div className="border-t border-white/10 py-6">
        <div className="container-wrap flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/50">
          <p>© {new Date().getFullYear()} WHO DAT Cares Foundation. A 501(c)(3) nonprofit organization.</p>
          <p>Reading with purpose. Writing the future.</p>
        </div>
      </div>
    </footer>
  );
}
